import { ApplicationRef, ComponentFactoryResolver, ComponentRef, EmbeddedViewRef, Injectable, Injector } from '@angular/core';
import { OrderFormNudgeComponent } from '../../components/nudges/order-form-nudge/order-form-nudge.component';
import { OrderNotificationParams, OrderToastConfig } from 'src/app/app.interfaces';
import { ConfigurationService } from '../configuration/configuration.service';
import { ConfigurationKeys } from 'src/app/app.structs';

@Injectable({
  providedIn: 'root'
})

export class OrderNotificationService {
  private container: HTMLElement;
  private readonly DISPLAY_TIME = 5000;
  private readonly MAX_NOTIFICATIONS = 20;
  private readonly UPDATE_WINDOW = 100;
  private masterOrderIdMap = new Map<string, number>();
  private activeNotifications: {
    componentRef: ComponentRef<OrderFormNudgeComponent>;
    timer?: any;
  }[] = [];

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector,
    private remoteConfigService: ConfigurationService
  ) {
    this.createNotificationContainer();
  }

  /**
   * Creates a fixed container for notifications
   */
  createNotificationContainer() {
    this.container = document.createElement('div');
    document.body.appendChild(this.container);
  }

  /**
   * Creates a new notification component
   * @param data Notification data to display
   * @returns ComponentRef of the created notification
   */
  createNotification(data: OrderNotificationParams): ComponentRef<OrderFormNudgeComponent> {
    const componentRef = this.componentFactoryResolver
      .resolveComponentFactory(OrderFormNudgeComponent)
      .create(this.injector);

    // Set data and close handler
    componentRef.instance.data = data;
    componentRef.instance.closed.subscribe(() => this.remove(componentRef));

    // Attach the component to the container
    this.appRef.attachView(componentRef.hostView);
    const domElement = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0];
    this.container.appendChild(domElement);

    return componentRef;
  }

  // Checks if the masterOrderId is already in the map, if it is, it clears the timer and adds a new one.
  // If it is not, it adds a new timer and adds the masterOrderId to the map.
  show(data: OrderNotificationParams) {
    const config : OrderToastConfig = this.remoteConfigService.remoteConfigCacheObj[ConfigurationKeys.OrderToastConfig];
    const updateWindow = config?.repeatToastTimer || this.UPDATE_WINDOW;    
    // Use GTT ID if available, otherwise use master_order_id
    const masterOrderId = data.is_gtt ? data.id?.toString() : data.master_order_id;
    let masterOrderIdTimerExists = false;

    if (this.masterOrderIdMap.has(masterOrderId)) {
      window.clearTimeout(this.masterOrderIdMap.get(masterOrderId));
      this.masterOrderIdMap.delete(masterOrderId);
      masterOrderIdTimerExists = true;
    }

    if (!masterOrderIdTimerExists) {
      const timer = window.setTimeout(() => {
        this.showNotification(data);
        this.masterOrderIdMap.delete(masterOrderId);
      }, updateWindow);
      this.masterOrderIdMap.set(masterOrderId, timer);
    } else {
      this.showNotification(data);
    }

  }

   /**
   * Shows a new notification
   * @param data Notification data to display
   * @returns ComponentRef of the created notification
   */
  showNotification(data: OrderNotificationParams) {
    const config: OrderToastConfig = this.remoteConfigService.remoteConfigCacheObj[ConfigurationKeys.OrderToastConfig];
    const displayTime = config?.displayTime || this.DISPLAY_TIME;
    const maxToasts = config?.maxToasts || this.MAX_NOTIFICATIONS;
    // if the number of notifications is greater than the max, remove the older notifications.
    if (this.activeNotifications.length >= maxToasts) {
      const oldestNotification = this.activeNotifications.pop();
      if (oldestNotification) {
        clearTimeout(oldestNotification.timer);
        this.appRef.detachView(oldestNotification.componentRef.hostView);
        oldestNotification.componentRef.destroy();
      }
    }
    const componentRef = this.createNotification(data);

    // Create notification with its own timer
    const notification = {
      componentRef,
      timer: setTimeout(() => this.remove(componentRef), displayTime)
    };

    // Add to front of array (newest first)
    this.activeNotifications.unshift(notification);
    this.updateNotificationPosition();

    return componentRef;
  }

  updateNotificationPosition() {
    const NOTIFICATION_HEIGHT = 140;
    this.activeNotifications.forEach((notification, index) => {
      const element = (notification.componentRef.hostView as EmbeddedViewRef<any>)
        .rootNodes[0] as HTMLElement;

      element.style.position = 'fixed';
      element.style.right = '20px';
      element.style.width = '300px';
      element.style.transition = 'all 0.3s ease';

      if (index === 0) {
        // Newest notification at front
        element.style.bottom = '20px';
        element.style.zIndex = '1000';
        element.style.opacity = '1';
        element.style.transform = 'none';
      } else {
        element.style.bottom = `${NOTIFICATION_HEIGHT + 30}px`;
        element.style.zIndex = `${999 - index}`;
        element.style.opacity = '0.95';
        element.style.transform = `translateY(${(index - 1) * -10}px)`;
      }
    });
  }

  /**
  * Removes a notification
  * @param componentRef Component reference to remove
  */
  remove(componentRef: ComponentRef<OrderFormNudgeComponent>) {
    const index = this.activeNotifications.findIndex(n => n.componentRef === componentRef);
    if (index > -1) {
      // Clear timer
      if (this.activeNotifications[index].timer) {
        clearTimeout(this.activeNotifications[index].timer);
      }

      // Remove notification
      this.activeNotifications.splice(index, 1);
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();

      // Update positions of remaining notifications
      this.updateNotificationPosition();
    }
  }
}